import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { PLATFORM_LABELS, SocialPlatformSelector, type SocialPlatformFilter } from "../SocialPlatformSelector";
import { SocialKpis } from "../SocialKpis";
import { SocialQueue } from "../SocialQueue";
import { SocialCommentsTable } from "../SocialCommentsTable";
import { SocialExecutiveSections } from "../SocialExecutiveSections";
import { SocialReplyModal } from "../SocialReplyModal";
import { SocialQualitativeReport, type PanelView } from "../SocialQualitativeReport";
import { SocialAuditToggle, SocialCustomDates, SocialDateRange, SocialEmojiToggle } from "../SocialToolbar";
import { readStoredRange, rangeToQuery, resolveRange, storeRange, type SocialRange } from "../socialRange";
import { containsEmoji } from "../socialFormat";
import { usePageHeaderSlots } from "../../../context/PageHeaderSlotContext";
import { SocialMobileNavDrawer } from "../SocialMobileNavDrawer";
import "../social.css";
import { fetchSocialOverview, fetchSocialQueue } from "../../../api";
import type {
  SocialDetailPayload,
  SocialOverviewPayload,
  SocialQueuePayload,
  SocialQueueItem,
  GertrudisAuditPayload,
} from "../../../types";

const AUDIT_VIEW_KEY = "wolf.social.audit.view";
const DESKTOP_QUERY = "(min-width: 1360px)";

function readAuditView(): PanelView {
  try {
    const v = window.localStorage.getItem(AUDIT_VIEW_KEY);
    if (v === "hidden" || v === "summary" || v === "full") return v;
  } catch {
    /* sin almacenamiento: vista por defecto */
  }
  return "summary";
}

function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return matches;
}

interface Props {
  data: SocialDetailPayload;
  clientSlug: string;
  onAuditGenerated: (audit: GertrudisAuditPayload) => void;
  range?: SocialRange;
  onRangeChange?: (range: SocialRange) => void;
  portalControls?: boolean;
}

/**
 * SocialTabSandbox
 *
 * Versión sandbox que aloja el layout anterior de Producción para experimentación y comparativa.
 */
export function SocialTabSandbox({
  data,
  clientSlug,
  onAuditGenerated,
  range: propRange,
  onRangeChange,
  portalControls = true,
}: Props) {
  const [platform, setPlatform] = useState<SocialPlatformFilter>("all");
  const [overview, setOverview] = useState<SocialOverviewPayload | null>(null);
  const [queue, setQueue] = useState<SocialQueuePayload | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queueOpen, setQueueOpen] = useState(true);
  const [selectedReplyItem, setSelectedReplyItem] = useState<SocialQueueItem | null>(null);
  const [localRange, setLocalRange] = useState<SocialRange>(readStoredRange);
  const range = propRange ?? localRange;
  const [auditView, setAuditViewState] = useState<PanelView>(readAuditView);
  const headerSlots = usePageHeaderSlots();
  const isDesktop = useIsDesktop();

  const changeRange = (next: SocialRange) => {
    if (onRangeChange) {
      onRangeChange(next);
    } else {
      setLocalRange(next);
      storeRange(next);
    }
  };

  const changeAuditView = (next: PanelView) => {
    setAuditViewState(next);
    try {
      window.localStorage.setItem(AUDIT_VIEW_KEY, next);
    } catch {
      /* ignorar */
    }
  };

  const query = useMemo(() => rangeToQuery(range), [range]);
  const queryKey = `${query.desde ?? ""}|${query.hasta ?? ""}`;
  const { desde: tableFrom, hasta: tableTo } = useMemo(() => resolveRange(range), [range]);
  const customIncomplete = range.preset === "custom" && !range.desde && !range.hasta;

  const recentComments = data.recentComments ?? [];
  const audit = data.audit;

  const availablePlatforms = useMemo<SocialPlatformFilter[]>(() => {
    const found = new Set<SocialPlatformFilter>();
    const checkPlat = (raw: unknown) => {
      const p = String(raw || "").toLowerCase();
      if (p === "meta_ig" || p.includes("ig") || p.includes("instagram")) found.add("meta_ig");
      if (p === "meta_fb" || p.includes("fb") || p.includes("facebook")) found.add("meta_fb");
      if (p === "gbp" || p.includes("google")) found.add("gbp");
    };

    if (data.summary && data.summary.length > 0) {
      for (const row of data.summary) {
        const count = Number(row.total_comments || 0);
        if (count > 0 || row.total_comments === undefined) {
          checkPlat(row.platform);
        }
      }
    }
    if (data.recentComments && data.recentComments.length > 0) {
      for (const row of data.recentComments) {
        checkPlat(row.platform);
      }
    }
    return Array.from(found);
  }, [data.summary, data.recentComments]);

  useEffect(() => {
    if (availablePlatforms.length === 1 && platform !== availablePlatforms[0]) {
      setPlatform(availablePlatforms[0]);
    } else if (availablePlatforms.length > 1 && platform !== "all" && !availablePlatforms.includes(platform)) {
      setPlatform("all");
    }
  }, [availablePlatforms, platform]);

  useEffect(() => {
    let active = true;
    setLoadingOverview(true);
    setLoadingQueue(true);
    setError(null);

    Promise.all([
      fetchSocialOverview(clientSlug, platform, query).then((res) => {
        if (active) setOverview(res);
      }),
      fetchSocialQueue(clientSlug, platform, query).then((res) => {
        if (active) setQueue(res);
      }),
    ])
      .catch((err) => {
        if (active) {
          console.error("Error al cargar datos del tab Social Sandbox:", err);
          setError(err instanceof Error ? err.message : "Error cargando métricas de Social.");
        }
      })
      .finally(() => {
        if (active) {
          setLoadingOverview(false);
          setLoadingQueue(false);
        }
      });

    return () => {
      active = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSlug, platform, queryKey]);

  const [filterContainsEmoji, setFilterContainsEmoji] = useState(false);

  const rangeComments = useMemo(() => {
    if (!recentComments.length) return [];
    return recentComments.filter((c) => {
      if (c.is_owner_author || c.is_reply) return false;
      const d = String(c.date || "").slice(0, 10);
      if (!d) return false;
      if (tableFrom && d < tableFrom) return false;
      if (tableTo && d > tableTo) return false;
      return true;
    });
  }, [recentComments, tableFrom, tableTo]);

  const filteredComments = useMemo(() => {
    if (!filterContainsEmoji) return rangeComments;
    return rangeComments.filter((c) => containsEmoji(c.message ? String(c.message) : ""));
  }, [rangeComments, filterContainsEmoji]);

  const dateControl = !propRange ? (
    <div className="social-topbar-controls">
      <SocialDateRange range={range} onChange={changeRange} />
      <SocialEmojiToggle
        active={filterContainsEmoji}
        onToggle={setFilterContainsEmoji}
      />
    </div>
  ) : (
    <div className="social-topbar-controls">
      <SocialEmojiToggle
        active={filterContainsEmoji}
        onToggle={setFilterContainsEmoji}
      />
    </div>
  );
  const auditToggle = audit?.insight ? (
    <SocialAuditToggle
      hidden={auditView === "hidden"}
      onToggle={() => changeAuditView(auditView === "hidden" ? "summary" : "hidden")}
    />
  ) : null;

  return (
    <div className="detail-stack social-tab-container">
      {portalControls && isDesktop && !propRange && headerSlots.left && createPortal(dateControl, headerSlots.left)}
      {portalControls && isDesktop && headerSlots.actions && (
        createPortal(
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {propRange && (
              <SocialEmojiToggle
                active={filterContainsEmoji}
                onToggle={setFilterContainsEmoji}
              />
            )}
            {auditToggle}
          </div>,
          headerSlots.actions,
        )
      )}
      {(!portalControls || !isDesktop || (!propRange && !headerSlots.left)) && (
        <div className="social-inline-toolbar">
          {dateControl}
          {auditToggle}
        </div>
      )}

      {!propRange && range.preset === "custom" && <SocialCustomDates range={range} onChange={changeRange} />}

      {!propRange && customIncomplete && (
        <div className="detail-notice" style={{ padding: "8px 12px", borderRadius: "8px", fontSize: "12px" }}>
          Elige las fechas del rango personalizado.
        </div>
      )}

      {/* 1. Gertrudis: acción IA arriba, colapsable (resumen / completo / oculto) */}
      <SocialQualitativeReport
        audit={audit}
        clientSlug={clientSlug}
        activePlatform={platform}
        comments={filteredComments}
        view={auditView}
        onViewChange={changeAuditView}
        onAuditGenerated={onAuditGenerated}
      />

      {/* 2. Selector único de plataforma */}
      <SocialPlatformSelector
        activePlatform={platform}
        onChange={setPlatform}
        availablePlatforms={availablePlatforms}
      />

      {error && (
        <div
          className="detail-notice is-danger"
          style={{ marginBottom: "16px", padding: "10px 14px", borderRadius: "8px" }}
        >
          {error}
        </div>
      )}

      {/* 3. Sección 01: Métricas ejecutivas dinámicas por plataforma */}
      <SocialKpis
        overview={overview}
        loading={loadingOverview}
        clientSlug={clientSlug}
        platform={platform}
        platformLabel={platform === "all" ? undefined : PLATFORM_LABELS[platform]}
        alertsOpen={queueOpen}
        onToggleAlerts={() => setQueueOpen((v) => !v)}
      />

      {/* 4. Secciones Editoriales 02 a 07 (Voz del paciente/cliente, Acción inmediata, Tendencia, Diagnóstico, Oportunidad, Plan de acción) */}
      <SocialExecutiveSections
        clientSlug={clientSlug}
        activePlatform={platform}
        comments={filteredComments}
        allComments={recentComments}
        queue={queue}
        overview={overview}
        onOpenReplyModal={(item) => setSelectedReplyItem(item)}
      />

      {/* 5. Tabla Detallada de Comentarios y Reseñas con filtros sticky, segmentador emojis y respuesta HITL */}
      <SocialCommentsTable
        comments={filteredComments}
        clientSlug={clientSlug}
        clientName={clientSlug === "wolf" ? "Wolf Digital" : undefined}
        activePlatform={platform}
        dateFrom={tableFrom}
        dateTo={tableTo}
        replyEnabled={Boolean(overview?.reply_enabled)}
        clientReplyConfigured={overview?.client_reply_configured !== undefined ? overview.client_reply_configured : Boolean(overview?.reply_enabled)}
        canReply={overview?.can_reply !== undefined ? overview.can_reply : Boolean(overview?.reply_enabled)}
      />

      {/* Modal unificado de respuesta HITL para acciones directas desde las cards ejecutivas */}
      <SocialReplyModal
        isOpen={Boolean(selectedReplyItem)}
        onClose={() => setSelectedReplyItem(null)}
        item={selectedReplyItem}
        clientSlug={clientSlug}
        onReplySuccess={() => {
          setSelectedReplyItem(null);
          fetchSocialOverview(clientSlug, platform, query).then(setOverview).catch(() => {});
          fetchSocialQueue(clientSlug, platform, query).then(setQueue).catch(() => {});
        }}
      />

      {/* Navegador Rápido Mobile de Secciones (Drawer hamburguesa/píldora inferior) */}
      <SocialMobileNavDrawer />
    </div>
  );
}
